





























































































































































import { Option } from '@/components/binding-wizard/model';
import { Action, RootAction } from '@/features/core/store';
import { AddToastMessageParams } from '@/features/core/store/toast';
import { GATEWAY_HEALTH_META } from '@/features/domain-ui/gateway-health/constants';
import { SPOT_HEALTH_META } from '@/features/domain-ui/spot-health/constants';
import { EditIvmApiInput, GatewayHealth, SpotHealth } from '@/types/iot-portal';
import { StringProp } from '@/util/prop-decorators';
import { at, isString } from 'lodash';
import { Component, Vue } from 'vue-property-decorator';
import editMutation from './edit.gql';
import removeMutation from './remove.gql';
import query from './view.gql';
import {
  AppCustomerIvmApiSettingsViewEditMutation,
  AppCustomerIvmApiSettingsViewEditMutationVariables,
} from './__generated__/AppCustomerIvmApiSettingsViewEditMutation';
import {
  AppCustomerIvmApiSettingsViewQuery,
  AppCustomerIvmApiSettingsViewQueryVariables,
} from './__generated__/AppCustomerIvmApiSettingsViewQuery';
import {
  AppCustomerIvmApiSettingsViewRemoveMutation,
  AppCustomerIvmApiSettingsViewRemoveMutationVariables,
} from './__generated__/AppCustomerIvmApiSettingsViewRemoveMutation';

type FormData = Pick<
  EditIvmApiInput,
  | 'webhookUrl'
  | 'label'
  | 'treeNodeIds'
  | 'enabled'
  | 'alertCategoryIds'
  | 'gatewayHealths'
  | 'spotHealths'
  | 'missedActivityLogEntry'
  | 'heatingSystem'
  | 'economicUnitReferenceAttributeDefinitionId'
  | 'propertyReferenceAttributeDefinitionId'
  | 'apartmentReferenceAttributeDefinitionId'
>;

@Component({
  apollo: {
    customers: {
      query,
      fetchPolicy: 'cache-and-network',
      variables(this: SettingsView): AppCustomerIvmApiSettingsViewQueryVariables {
        return { customerId: this.customerId, vendorApiId: this.vendorApiId };
      },
    },
  },
  data() {
    return { customers: undefined };
  },
})
export default class SettingsView extends Vue {
  @StringProp(true)
  private readonly customerId!: string;

  @StringProp(true)
  private readonly vendorApiId!: string;

  @RootAction
  private readonly ADD_TOAST_MESSAGES!: Action<AddToastMessageParams, void>;

  private readonly customers?: AppCustomerIvmApiSettingsViewQuery['customers'];

  private async edit(input: FormData): Promise<void> {
    const ivmApiId = this.customers?.first.ivmApis.first?.id;
    if (ivmApiId === undefined) {
      return;
    }

    const { data } = await this.$apollo.mutate<
      AppCustomerIvmApiSettingsViewEditMutation,
      AppCustomerIvmApiSettingsViewEditMutationVariables
    >({
      mutation: editMutation,
      variables: {
        input: {
          ivmApiId,
          ...input,
        },
      },
    });

    if (!data) {
      throw new Error('Die Einstellungen der IVM Professional® Integration konnten nicht gespeichert werden');
    }

    const text = `Die Einstellungen der IVM Professional® Integration wurden gespeichert`;
    this.ADD_TOAST_MESSAGES({ messages: [{ text }] });
  }

  private async remove(): Promise<void> {
    const ivmApiId = this.customers?.first.ivmApis.first?.id;
    if (ivmApiId === undefined) {
      return;
    }

    if (!window.confirm('Die IVM Professional® Integration wurde entfernt')) {
      return;
    }

    const { data } = await this.$apollo.mutate<
      AppCustomerIvmApiSettingsViewRemoveMutation,
      AppCustomerIvmApiSettingsViewRemoveMutationVariables
    >({
      mutation: removeMutation,
      variables: { input: { ivmApiId } },
    });

    if (!data) {
      throw new Error('Die IVM Professional® Integration konnte nicht entfernt werden');
    }

    const text = `Die IVM Professional® Integration wurde entfernt`;
    this.ADD_TOAST_MESSAGES({ messages: [{ text }] });

    this.$router.push({ name: 'AppCustomer/Customer/VendorApis' });
  }

  private get gatewayHealthOptions(): Option[] {
    return at(GATEWAY_HEALTH_META, GatewayHealth.CRITICAL, GatewayHealth.DEGRADED);
  }

  private get spotHealthOptions(): Option[] {
    return at(SPOT_HEALTH_META, SpotHealth.CRITICAL, SpotHealth.DEGRADED);
  }

  private get rootIds(): string[] {
    return this.customers === undefined ? [] : [this.customers.first.rootDirectory.id];
  }

  private pluckIds<T>(objects: { id: T }[] | null): T[] {
    return objects === null ? [] : objects.map(({ id }) => id);
  }

  private get complete(): boolean {
    if (this.customers === undefined) {
      return true;
    }
    const {
      webhookUrl,
      economicUnitReferenceAttributeDefinition,
      propertyReferenceAttributeDefinition,
      apartmentReferenceAttributeDefinition,
    } = this.customers.first.ivmApis.first;
    return (
      webhookUrl !== null &&
      economicUnitReferenceAttributeDefinition !== null &&
      propertyReferenceAttributeDefinition !== null &&
      apartmentReferenceAttributeDefinition !== null
    );
  }

  private isNonEmptyString(value: unknown): boolean {
    return isString(value) && value.length > 0;
  }
}
